import type { EncounterState, SpatialState } from "../types/game";

interface AmbientLine {
  speaker: string;
  role: string;
  text: string;
}

// Templates organized by escalation level
const ROUTINE_LINES: AmbientLine[] = [
  { speaker: "Shin", role: "science", text: "Sensors nominal. Long-range scans show no anomalies." },
  { speaker: "Shin", role: "science", text: "Running background spectral analysis on the system's star." },
  { speaker: "Calloway", role: "engineering", text: "All systems operating within normal parameters." },
  { speaker: "Calloway", role: "engineering", text: "Running diagnostics on the warp coils. Everything looks clean." },
  { speaker: "Okafor", role: "helm", text: "Holding position steady." },
  { speaker: "Okafor", role: "helm", text: "Navigation systems synced. Star charts updated." },
  { speaker: "Reyes", role: "communications", text: "Monitoring standard frequencies. Nothing unusual." },
  { speaker: "Reyes", role: "communications", text: "Subspace channels are quiet." },
  { speaker: "Vasek", role: "first_officer", text: "Status reports are all green across departments." },
];

const TENSE_LINES: AmbientLine[] = [
  { speaker: "Toraq", role: "tactical", text: "Weapons are charged and ready. Maintaining tactical readiness." },
  { speaker: "Toraq", role: "tactical", text: "Watching their weapons signatures closely. No changes yet." },
  { speaker: "Shin", role: "science", text: "Monitoring their power output. They're running hot but stable." },
  { speaker: "Shin", role: "science", text: "Picking up elevated energy readings from the hostile contacts." },
  { speaker: "Okafor", role: "helm", text: "Ready to maneuver on your word, Captain." },
  { speaker: "Okafor", role: "helm", text: "Plotting escape vectors, just in case." },
  { speaker: "Reyes", role: "communications", text: "Intercepting fragments of encrypted chatter between the hostiles." },
  { speaker: "Reyes", role: "communications", text: "They're talking to each other on a tight beam. Can't crack it yet." },
  { speaker: "Calloway", role: "engineering", text: "I've got damage control teams on standby." },
  { speaker: "Vasek", role: "first_officer", text: "The situation is fluid. Recommend we stay alert." },
];

const COMBAT_LINES: AmbientLine[] = [
  { speaker: "Toraq", role: "tactical", text: "They're regrouping. Expect another attack run." },
  { speaker: "Toraq", role: "tactical", text: "Multiple contacts still active. Tracking all hostiles." },
  { speaker: "Shin", role: "science", text: "Detecting fluctuations in their shield harmonics." },
  { speaker: "Shin", role: "science", text: "Their formation is shifting — they may be flanking us." },
  { speaker: "Okafor", role: "helm", text: "Holding course. Ready for evasive on your mark." },
  { speaker: "Calloway", role: "engineering", text: "Shields are holding but I can't keep this up forever." },
  { speaker: "Calloway", role: "engineering", text: "Power consumption is spiking. We need to end this." },
  { speaker: "Reyes", role: "communications", text: "They're coordinating fire — I can hear targeting data on their comms." },
  { speaker: "Vasek", role: "first_officer", text: "We can't sustain this engagement indefinitely." },
];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Phase 4.4: ambient chatter is OFF by default. Player primacy means the
// bridge stays quiet unless the captain or world causes a beat. Re-enable
// only via explicit opt-in (e.g., a future "active bridge" toggle).
const ENABLED = false;

export class AmbientEventSystem {
  private idleAccumulator = 0;
  private nextEventDelay = 15 + Math.random() * 10;
  private recentLines = new Set<string>();
  private getEncounterState: () => EncounterState | null;
  private getExecutor: () => { currentSpatialState: SpatialState } | null;
  private addEntry: (type: "dialogue", text: string, speaker?: string, role?: string) => void;

  constructor(
    getEncounterState: () => EncounterState | null,
    getExecutor: () => { currentSpatialState: SpatialState } | null,
    addEntry: (type: "dialogue", text: string, speaker?: string, role?: string) => void,
  ) {
    this.getEncounterState = getEncounterState;
    this.getExecutor = getExecutor;
    this.addEntry = addEntry;
  }

  onPlayerAction() {
    this.idleAccumulator = 0;
  }

  tick(deltaSeconds: number) {
    if (!ENABLED) return;
    this.idleAccumulator += deltaSeconds;
    if (this.idleAccumulator < this.nextEventDelay) return;

    this.idleAccumulator = 0;
    this.nextEventDelay = 15 + Math.random() * 10;

    const encounter = this.getEncounterState();
    if (!encounter) return;

    const line = this.pickLine(encounter);
    if (!line) return;

    const key = `${line.speaker}:${line.text.slice(0, 40)}`;
    if (this.recentLines.has(key)) return; // avoid repeats
    this.recentLines.add(key);
    if (this.recentLines.size > 20) {
      const first = this.recentLines.values().next().value;
      if (first) this.recentLines.delete(first);
    }

    this.addEntry("dialogue", line.text, line.speaker, line.role);
  }

  private pickLine(encounter: EncounterState): AmbientLine | null {
    const esc = encounter.escalation ?? "routine";
    const executor = this.getExecutor();

    // Dynamic lines based on current state
    const dynamic: AmbientLine[] = [];
    if (executor) {
      const spatial = executor.currentSpatialState;
      const hostiles = spatial.entities.filter(e => e.type === "ship" && e.condition !== "destroyed");
      const wrecks = spatial.entities.filter(e => e.type === "debris");

      if (wrecks.length > 0) {
        dynamic.push({ speaker: "Shin", role: "science", text: `Detecting ${wrecks.length} debris field${wrecks.length > 1 ? "s" : ""} on sensors. Could be worth investigating.` });
      }
      if (hostiles.length > 0 && (esc === "tense" || esc === "cautious")) {
        const h = pickRandom(hostiles);
        dynamic.push({ speaker: "Toraq", role: "tactical", text: `${h.name} is still out there. Bearing hasn't changed.` });
      }
      if (spatial.playerShip.shields === "up" && esc === "routine") {
        dynamic.push({ speaker: "Calloway", role: "engineering", text: "Shields are still up, Captain. Want me to stand them down? They're drawing power." });
      }
    }

    // Pick from appropriate pool
    let pool: AmbientLine[];
    switch (esc) {
      case "combat":
      case "critical":
        pool = [...COMBAT_LINES, ...dynamic];
        break;
      case "confrontational":
      case "tense":
        pool = [...TENSE_LINES, ...dynamic];
        break;
      default:
        pool = [...ROUTINE_LINES, ...dynamic];
    }

    return pool.length > 0 ? pickRandom(pool) : null;
  }
}
